import { useQuery } from '@tanstack/react-query';

import { SelectOption } from '@/shared/types';

import { getCitySelect, getDistrictSelect, getRegionSelect, getStreetSelect } from '@/api/apiService';
import { queryKeys } from '@/api/queryKeys';
import { DictionaryStreet } from '@/api/types';

interface HookProps {
  selectedRegionId: Nullable<string>;
  selectedDistrictId: Nullable<string>;
  selectedSettlementId: Nullable<string>;
}

interface HookReturn {
  optionsRegion: SelectOption[];
  optionsDistrict: SelectOption[];
  optionsSettlement: SelectOption[];
  optionsStreet: Array<SelectOption & Record<'postCode', DictionaryStreet['postCode']>>;
}

export const useAddressOptions = ({
  selectedRegionId,
  selectedDistrictId,
  selectedSettlementId,
}: HookProps): HookReturn => {
  const { data: optionsRegion = [] } = useQuery({
    queryKey: queryKeys.address.regions(),
    queryFn: () => getRegionSelect(),
  });

  const { data: optionsDistrict = [] } = useQuery({
    queryKey: queryKeys.address.districts(selectedRegionId ?? ''),
    queryFn: () => getDistrictSelect(selectedRegionId!),
    enabled: !!selectedRegionId,
  });

  const { data: optionsSettlement = [] } = useQuery({
    queryKey: queryKeys.address.settlements(selectedDistrictId ?? ''),
    queryFn: () => getCitySelect(selectedDistrictId),
    enabled: !!selectedDistrictId,
  });

  const { data: optionsStreet = [] } = useQuery({
    queryKey: queryKeys.address.streets(selectedSettlementId ?? ''),
    queryFn: () => getStreetSelect(selectedSettlementId!),
    enabled: !!selectedSettlementId,
  });

  return {
    optionsRegion,
    optionsDistrict,
    optionsSettlement,
    optionsStreet,
  };
};
